/**
 * localService.js — Wraps window.menuronDesktop.local.* calls.
 * Mirrors the exact same method signatures as supabase.js services
 * so components using dataService.js need zero changes.
 */

const d = window.menuronDesktop.local

export const restaurantService = {
  getMenu: (restaurantId) => d.getMenu(restaurantId),
  getTables: (restaurantId) => d.getTables(restaurantId),
  getOrders: (restaurantId) => d.getOrders(restaurantId),
  updateOrderStatus: (orderId, status) => d.updateOrderStatus(orderId, status),

  // These are only used on the web portal, not POS — fall through gracefully
  createOrder: () => ({ data: null, error: { message: 'Not supported in desktop mode' } }),
  createOrderItems: () => ({ data: null, error: { message: 'Not supported in desktop mode' } }),
  waiterConfirmOrder: () => ({ data: null, error: null }),
  getOrderById: () => ({ data: null, error: null }),
  // Menu CRUD — works offline, syncs to Supabase when back online
  createCategory: (restaurantId, data) => d.createCategory(restaurantId, data),
  updateCategory: (categoryId, updates) => d.updateCategory(categoryId, updates),
  deleteCategory: (categoryId) => d.deleteCategory(categoryId),
  createMenuItem: (restaurantId, data) => d.createMenuItem(restaurantId, data),
  updateMenuItem: (itemId, updates) => d.updateMenuItem(itemId, updates),
  deleteMenuItem: (itemId) => d.deleteMenuItem(itemId),
  getOrdersForPeriod: (restaurantId, startDate, endDate) =>
    d.getOrdersForReport(restaurantId, startDate, endDate),
}

export const billingService = {
  getSettings: (restaurantId) => d.getBillingSettings(restaurantId),
  getBillingDefaults: async (restaurantId) => {
    const res = await d.getBillingDefaults(restaurantId)
    if (res?.error || !res?.data) return res
    const row = res.data
    return {
      data: {
        gstEnabled:             !!row.gst_enabled,
        gstPercent:             parseFloat(row.gst_percent) || 0,
        gstin:                  row.gstin || null,
        serviceChargeEnabled:   !!row.service_charge_enabled,
        serviceChargePercent:   parseFloat(row.service_charge_percent) || 0,
        defaultDiscountEnabled: !!row.discount_enabled,
        defaultDiscountType:    row.discount_type || 'NONE',
        defaultDiscountValue:   parseFloat(row.discount_value) || 0,
      },
      error: null,
    }
  },
  saveSettings: (restaurantId, settings) => d.saveBillingSettings(restaurantId, settings),
  // Pass offline flag so masterDb can prefix invoice number with OD- when offline
  claimNextInvoiceNumber: (restaurantId) => {
    const { isOnline } = require('./syncService')
    return d.claimNextInvoiceNumber(restaurantId, !isOnline()).then(res => {
      if (res?.data?.invoiceNumber) return { invoiceNumber: res.data.invoiceNumber, error: null }
      return { invoiceNumber: null, error: res?.error || { message: 'Failed to claim invoice number' } }
    })
  },
}

export const posService = {
  generateOrderNumber: () => ({ data: null, error: null }),
  // When online: claim DA-prefixed number from Supabase atomically.
  // When offline: masterDb generates OD-prefixed number from local SQLite.
  createCounterOrder: async (restaurantId, opts) => {
    let orderNumber = null
    const { isOnline } = require('./syncService')
    if (isOnline()) {
      try {
        const { supabase } = require('./supabase')
        const { data, error } = await supabase.rpc('claim_next_order_number', {
          p_restaurant_id: restaurantId,
          p_prefix: 'DA',
        })
        if (!error && data) orderNumber = data
      } catch (e) {}
    }
    return d.createCounterOrder(restaurantId, { ...opts, orderNumber })
  },
  getOrderById: async (orderId) => {
    const res = await d.getOrderById(orderId)
    return { data: res?.data || null, error: res?.error || null }
  },
  getCounterOrders: (restaurantId, opts) => d.getCounterOrders(restaurantId, opts),
  getOrdersForReport: (restaurantId, startDate, endDate, managerId) =>
    d.getOrdersForReport(restaurantId, startDate, endDate, managerId),
  getManagerActivityReport: (restaurantId, startDate, endDate) =>
    d.getManagerActivityReport(restaurantId, startDate, endDate),
  getInvoices: (restaurantId) => d.getInvoices(restaurantId),
  confirmOrder: (orderId) => d.confirmOrder(orderId),
  updateStatus: (orderId, status) => d.updateStatus(orderId, status),
  billOrder: (orderId, billingData) => d.billOrder(orderId, billingData),
  updateOrderItems: (orderId, items) => d.updateOrderItems(orderId, items),
  markAsMerged: (orderId, invoiceNumber) => d.markAsMerged(orderId, invoiceNumber),
  markPaid: (orderId, paymentMethod) => d.markPaid(orderId, paymentMethod),
  deleteOrder: (orderId) => d.deleteOrder(orderId),
  markOrderInvalid: (orderId) => d.markOrderInvalid(orderId),
  updatePaymentMethod: (orderId, paymentMethod) => d.updatePaymentMethod(orderId, paymentMethod),
  claimKotNumber: (orderId) => d.claimKotNumber(orderId),
}

export const analyticsService = {
  getOrdersForPeriod: (restaurantId, startDate, endDate) =>
    d.getOrdersForReport(restaurantId, startDate, endDate),
}

export const crmService = {
  searchCustomers: (restaurantId, query) => d.searchCustomers(restaurantId, query),
  getCustomers: (restaurantId) => d.getCustomers(restaurantId),
  createCustomer: (restaurantId, data) => d.createCustomer(restaurantId, data),
  updateCustomer: (customerId, fields) => d.updateCustomer(customerId, fields),
  deleteCustomer: (customerId) => d.deleteCustomer(customerId),
  getCustomerOrders: (customerId) => d.getCustomerOrders(customerId),
}

export const menuService = {
  createCategory: (data) => d.createCategory(data.restaurant_id, data),
  updateCategory: (categoryId, updates) => d.updateCategory(categoryId, updates),
  deleteCategory: (categoryId) => d.deleteCategory(categoryId),
  createMenuItem: (data) => d.createMenuItem(data.restaurant_id, data),
  updateMenuItem: (itemId, updates) => d.updateMenuItem(itemId, updates),
  deleteMenuItem: (itemId) => d.deleteMenuItem(itemId),
}

export const staffService = {
  getManagers: (restaurantId) => d.getManagers(restaurantId),
  getWaiters: (restaurantId) => d.getWaiters(restaurantId),
  // Adding managers/waiters requires internet — block offline
  addWaiter: () => ({ data: null, error: { message: 'Adding staff requires internet connection' } }),
  updateWaiterStatus: () => ({ error: { message: 'Requires internet connection' } }),
  deleteWaiter: () => ({ error: { message: 'Requires internet connection' } }),
  createManager: () => ({ data: null, error: { message: 'Adding managers requires internet connection' } }),
  updateManagerStatus: () => ({ error: { message: 'Requires internet connection' } }),
  deleteManager: () => ({ error: { message: 'Requires internet connection' } }),
  getRestaurantManagers: (restaurantId) => d.getManagers(restaurantId),
  getRestaurantWaiters: (restaurantId) => d.getWaiters(restaurantId),
}
